import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { adminAPI } from '@/lib/adminApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Package, Loader2, AlertCircle, ArrowLeft, ArrowRight,
  Clock, Send, PlayCircle, CheckCircle, XCircle, Eye,
  ChevronLeft, ChevronRight
} from 'lucide-react';

// Service type labels
const SERVICE_TYPE_LABELS = {
  photo_restoration: 'Photo Restoration',
  lora_training: 'LoRA Training',
  photo_scanning: 'Photo Scanning',
  digital_persona: 'Digital Persona',
  general_upload: 'General Upload',
};

// Status config
const STATUS_CONFIG = {
  requested: { label: 'Requested', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Send },
  scheduled: { label: 'Scheduled', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Clock },
  shot: { label: 'Shot', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: PlayCircle },
  editing: { label: 'Editing', className: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30', icon: PlayCircle },
  draft: { label: 'Draft', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: Clock },
  received: { label: 'Received', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Send },
  in_progress: { label: 'In Progress', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: PlayCircle },
  delivered: { label: 'Delivered', className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  canceled: { label: 'Canceled', className: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
  // Legacy statuses
  pending: { label: 'Pending', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  paid: { label: 'Paid', className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  completed: { label: 'Completed', className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
};

// Status filter options
const STATUS_FILTERS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'requested', label: 'Requested' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'draft', label: 'Draft' },
  { value: 'received', label: 'Received' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'canceled', label: 'Canceled' },
];

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function AdminOrdersList() {
  const { isAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const pageSize = 20;

  useEffect(() => {
    if (isAdmin) {
      loadOrders();
    }
  }, [isAdmin, page, statusFilter]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const filterValue = statusFilter === 'all' ? null : statusFilter;
      const data = await adminAPI.listAllOrders(page, pageSize, filterValue);
      setOrders(data.orders || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  // Redirect non-admins
  if (!isAdmin) {
    return <Navigate to="/orders" replace />;
  }

  return (
    <div className="min-h-screen bg-[#050816]" data-testid="admin-orders-list">
      {/* Header */}
      <div className="bg-[#0f1724] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Button 
            asChild
            variant="ghost" 
            className="mb-4 text-[#E8E4DE]/60 hover:text-white hover:bg-white/5 -ml-2"
          >
            <Link to="/admin">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Link>
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#C6A14A]/20 rounded-full flex items-center justify-center">
              <Package className="h-6 w-6 text-[#C6A14A]" />
            </div>
            <div>
              <h1 className="text-3xl font-light text-white tracking-wide">All Orders</h1>
              <p className="text-[#E8E4DE]/60 mt-1">
                {total} total order{total !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Filters */}
        <Card className="bg-[#0f1724] border-white/10 mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm text-[#E8E4DE]/60">Filter by status:</span>
                <Select
                  value={statusFilter}
                  onValueChange={(value) => {
                    setStatusFilter(value);
                    setPage(1);
                  }}
                >
                  <SelectTrigger 
                    className="w-[180px] bg-[#050816]/50 border-white/10 text-white"
                    data-testid="status-filter"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f1724] border-white/10">
                    {STATUS_FILTERS.map((status) => (
                      <SelectItem 
                        key={status.value} 
                        value={status.value}
                        className="text-white hover:bg-white/10"
                      >
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button
                onClick={loadOrders}
                variant="outline"
                className="border-white/10 text-[#E8E4DE]"
                disabled={loading}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error State */}
        {error && (
          <div className="flex items-center gap-2 p-3 mb-6 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Orders List */}
        <Card className="bg-[#0f1724] border-white/10">
          <CardHeader>
            <CardTitle className="text-xl text-white">Orders</CardTitle>
            <CardDescription className="text-[#E8E4DE]/60">
              Manage and view all customer orders
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin" />
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-[#E8E4DE]/30 mx-auto mb-4" />
                <p className="text-[#E8E4DE]/60">No orders found</p>
                {statusFilter !== 'all' && (
                  <Button
                    variant="link"
                    className="text-[#C6A14A] mt-2"
                    onClick={() => setStatusFilter('all')}
                  >
                    Clear filter
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {orders.map((order) => {
                  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.draft;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <Link
                      key={order.id}
                      to={`/admin/orders/${order.id}`}
                      className="flex items-center justify-between p-4 bg-[#050816]/30 rounded-lg hover:bg-[#050816]/50 transition-colors group"
                      data-testid={`order-row-${order.id}`}
                    >
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <div className="w-10 h-10 bg-[#C6A14A]/10 rounded-full flex items-center justify-center shrink-0">
                          <StatusIcon className="h-5 w-5 text-[#C6A14A]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-white font-medium truncate">
                              {order.title || SERVICE_TYPE_LABELS[order.service_type] || order.service_type}
                            </p>
                            <Badge className={statusConfig.className}>
                              {statusConfig.label}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-[#E8E4DE]/50 mt-1">
                            <span className="truncate">{order.user_id?.substring(0, 8)}...</span>
                            <span>•</span>
                            <span>{formatDate(order.created_at)}</span>
                            <span>•</span>
                            <span>{SERVICE_TYPE_LABELS[order.service_type] || order.service_type}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 shrink-0 ml-4">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-[#C6A14A] hover:text-[#D4AF5A] hover:bg-[#C6A14A]/10 opacity-0 group-hover:opacity-100 transition-opacity"
                          asChild
                        >
                          <span>
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </span>
                        </Button>
                        <ArrowRight className="h-4 w-4 text-[#C6A14A] opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
                <p className="text-sm text-[#E8E4DE]/60">
                  Page {page} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-white/10 text-[#E8E4DE]"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-white/10 text-[#E8E4DE]"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages || loading}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
